/**
 * Public auth pages (login / register / forgot / activate) share two static
 * chunks of chrome: the bottom legal footer and the left "story" aside.
 * Both are pure HTML factories — no state, no DOM access, just template
 * literal output — so they live in lib/ and are imported by the bundle
 * HEADER. Route files (login/register/forgot-password/account-activate)
 * call them by name.
 */

import { LS } from "./constants.js";
import {
  BRAND_PRODUCT,
  BRAND_COMPANY_EYEBROW,
  BRAND_COMPANY_LINE,
  BRAND_FOOTER_LEGAL,
  BRAND_LOGO_SRC,
  BRAND_LOGO_ALT,
  brandWordmarkStackHtml,
} from "./brand.js";

/** CC auth shell: full-viewport glass background + light theme (login/register/etc.). */
export function activateCommandCenterAuthLayout() {
  try {
    document.body.dataset.ccAuth = "1";
    document.documentElement.dataset.theme = "light";
  } catch (_) {}
}

export function deactivateCommandCenterAuthLayout() {
  try {
    delete document.body.dataset.ccAuth;
    const saved = localStorage.getItem(LS.theme);
    const prefersDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
    const restoreTheme = globalThis.setTheme;
    if (typeof restoreTheme === "function") {
      restoreTheme(saved || (prefersDark ? "dark" : "light"));
    }
  } catch (_) {}
}

/**
 * CC-styled auth wrapper — keeps original auth-surface split layout (aside + form).
 * @param {"login"|"register"|"recovery"|"activate"} kind
 * @param {string} mainHtml — auth-card HTML
 */
export function buildCommandCenterAuthSurfaceHtml(kind, mainHtml) {
  return `
<div class="croc-cc-auth croc-cc" role="main">
  <div class="croc-cc-auth-flow" aria-hidden="true"></div>
  <div class="croc-cc-ambient croc-cc-auth-ambient" aria-hidden="true"></div>
  <div class="auth-surface">
    ${authAsideHtml(kind)}
    <div class="auth-surface__body">
      <div class="auth-surface__inner">
        ${mainHtml}
        ${authSiteFooterHtml()}
      </div>
    </div>
  </div>
</div>`;
}

export function ccAuthFooterHtml() {
  return authSiteFooterHtml();
}

export function authSiteFooterHtml() {
  return `
      <footer class="site-footer site-footer--auth" aria-label="Page footer">
        <div class="site-footer__row site-footer__row--auth">
          <p class="site-footer__legal">${BRAND_FOOTER_LEGAL}</p>
        </div>
      </footer>`;
}

/** Public auth: left story panel + right form (v3 layout) */
export function authAsideHtml(kind) {
  const m = {
    login: {
      t: "Where the field becomes accountable",
      d: BRAND_COMPANY_LINE,
      items: [
        "Notify first — humans decide",
        "Account-isolated edge fleet",
        "Response you can prove",
      ],
    },
    register: {
      t: "Enter the response chain",
      d: "Verify your email, then command an account-safe edge fleet.",
      items: ["Isolated account data", "Verification + cooldown", "No cross-account bleed"],
    },
    recovery: {
      t: "Recover access",
      d: "We send a one-time code to the email on file for this account.",
      items: ["Match username to email", "Code from your inbox", "Set a new password here"],
    },
    activate: {
      t: "Activate your place in the chain",
      d: "An administrator created your user — confirm with the email we sent you.",
      items: ["One-time code", "Same inbox as the invite", "Then use Sign in"],
    },
  };
  const c = m[kind] || m.login;
  return `
      <aside class="auth-surface__side" aria-label="${BRAND_PRODUCT}">
        <div class="auth-surface__side-main">
        <div class="auth-surface__side-content">
          <div class="auth-surface__company" lang="en">
            <p class="auth-surface__company-eyebrow">${BRAND_COMPANY_EYEBROW}</p>
            ${brandWordmarkStackHtml()}
          </div>
          <h2 class="auth-surface__headline">${c.t}</h2>
          <p class="auth-surface__lede">${c.d}</p>
          <ul class="auth-surface__bullets" role="list">
            ${c.items.map((x) => `<li>${x}</li>`).join("")}
          </ul>
        </div>
        </div>
        <div class="auth-surface__side-foot croc-cc-auth-side-foot" role="group" aria-label="${BRAND_PRODUCT}">
          <div class="auth-surface__brand-mark croc-cc-auth-side-logo">
            <img
              class="auth-surface__brand-mark-img"
              src="${BRAND_LOGO_SRC}"
              alt="${BRAND_LOGO_ALT}"
              width="120"
              height="120"
              decoding="async"
            />
          </div>
        </div>
      </aside>`;
}
